import { t } from 'i18next';
import { capitalize } from 'lodash';
import { useMutation, useQuery } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors, useInfiniteQuery } from 'core/hooks';
import { GetManyResult, GetOneResult, IInfiniteQueryOptions, IRequestPayload } from 'core/types';
import { PricingProfilePricingType } from 'pricing/enums';
import { pricingProfileService } from 'pricing/services';
import { ICorporatePricingProfile, IPricingProfile } from 'pricing/types';

interface IPricingProfileActions {
  type?: PricingProfilePricingType;
  selectionCount?: number;
}

export const useGetPricingProfiles = (
  requestPayload?: IRequestPayload,
  enabled = true,
  onSuccess?: (data: { records: IPricingProfile[]; count: number }) => void
): GetManyResult<IPricingProfile> => {
  const { data } = useQuery(
    ['pricingProfiles', requestPayload],
    () => pricingProfileService.getPricingProfiles(requestPayload),
    {
      enabled,
      onSuccess: (data) => {
        if (onSuccess) {
          onSuccess({ records: data?.records ?? [], count: data?.count ?? 0 });
        }
      },
    }
  );

  return {
    records: data?.records ?? [],
    count: data?.count ?? 0,
  };
};

export const useGetPricingProfile = (
  criteria: { id: number },
  enabled = true
): GetOneResult<IPricingProfile> => {
  const { data, isLoading, isError } = useQuery(
    ['pricingProfile', criteria],
    ({ signal }) => pricingProfileService.getPricingProfile(criteria, signal),
    { enabled }
  );

  return {
    record: data?.record ?? null,
    isLoading,
    isError,
  };
};

export const usePricingProfileActions = ({
  type = PricingProfilePricingType.PRICING,
  selectionCount,
}: IPricingProfileActions) => {
  return {
    ...useUpsertPricingProfile(type),
    ...useUpsertPricingProfiles(type),
    ...useRemovePricingProfiles(type, selectionCount),
  };
};

const useUpsertPricingProfile = (type: PricingProfilePricingType) => {
  const capitalizedType = capitalize(type);

  const { errors } = useHandleErrors({
    success: ({ pricingProfile }) =>
      pricingProfile.id
        ? { message: t(`message${capitalizedType}ProfileUpdateSuccess`) }
        : { message: t(`message${capitalizedType}ProfileCreateSuccess`) },
    loading: ({ pricingProfile }) =>
      pricingProfile.id
        ? { message: t(`message${capitalizedType}ProfileUpdateInProgress`) }
        : { message: t(`message${capitalizedType}ProfileCreateInProgress`) },
    error: ({ pricingProfile }) =>
      pricingProfile.id
        ? { title: t(`errorTitle${capitalizedType}ProfileUpdate`) }
        : { title: t(`errorTitle${capitalizedType}ProfileCreate`) },
    position: NotificationPosition.FORM,
    invalidateQueries: ['pricingProfile', 'corporatePricingProfiles'],
  });

  const { mutateAsync } = useMutation((data) => pricingProfileService.upsertPricingProfile(data), {
    ...errors,
  });

  const upsertPricingProfile = (data) => mutateAsync(data);

  return { upsertPricingProfile };
};

const useUpsertPricingProfiles = (type: PricingProfilePricingType) => {
  const capitalizedType = capitalize(type);

  const { errors } = useHandleErrors({
    success: { message: t(`message${capitalizedType}ProfilesUpdateSuccess`) },
    loading: { message: t(`message${capitalizedType}ProfilesUpdateInProgress`) },
    error: { title: t(`errorTitle${capitalizedType}ProfilesUpdate`) },
    position: NotificationPosition.FORM,
    invalidateQueries: ['pricingProfile', 'corporatePricingProfiles'],
  });

  const { mutateAsync } = useMutation((data) => pricingProfileService.upsertPricingProfiles(data), {
    ...errors,
  });

  return { upsertPricingProfiles: mutateAsync };
};

const useRemovePricingProfiles = (type: PricingProfilePricingType, selectionCount: number) => {
  const { errors } = useHandleErrors({
    success: {
      message: t(`message${capitalize(type)}ProfilesRemoveSuccess`, { count: selectionCount }),
    },
    loading: {
      message: t(`message${capitalize(type)}ProfilesRemoveInProgress`, { count: selectionCount }),
    },
    error: {
      title: t(`errorTitle${capitalize(type)}ProfilesRemove`, { count: selectionCount }),
    },
    position: NotificationPosition.TOP,
  });

  const { mutateAsync } = useMutation((data) => pricingProfileService.removePricingProfiles(data), {
    ...errors,
  });

  const removePricingProfiles = (data) => mutateAsync(data);

  return { removePricingProfiles };
};

export const useGetCorporatePricingProfiles = <T = ICorporatePricingProfile>(
  requestPayload?: IRequestPayload,
  options: IInfiniteQueryOptions<T> = { fetchAllPages: true }
) =>
  useInfiniteQuery<T>(
    (params, signal) =>
      pricingProfileService.getCorporatePricingProfiles<T>(
        { ...requestPayload, ...params },
        signal
      ),
    'corporatePricingProfiles',
    requestPayload,
    options
  );
